import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useReducedMotion } from "framer-motion";

export type UpdateStage =
	| "idle"
	| "signing"
	| "submitting"
	| "consensus"
	| "confirmed"
	| "error";

const STAGE_LABEL: Record<Exclude<UpdateStage, "idle">, string> = {
	signing: "Signing…",
	submitting: "Submitting…",
	consensus: "Awaiting consensus…",
	confirmed: "Confirmed",
	error: "Failed — retry",
};

const STAGE_ORDER: UpdateStage[] = [
	"signing",
	"submitting",
	"consensus",
	"confirmed",
];

export function UpdateStageLoader({
	stage,
	className,
	compact,
}: {
	stage: UpdateStage;
	className?: string;
	compact?: boolean;
}) {
	const reduced = useReducedMotion();
	if (stage === "idle") return null;

	const idx = STAGE_ORDER.indexOf(stage === "error" ? "submitting" : stage);
	const progress =
		stage === "confirmed" ? 100 : stage === "error" ? 40 : Math.max(8, ((idx + 1) / 4) * 100);

	return (
		<div className={cn("w-full space-y-2", className)} role="status" aria-live="polite">
			<div className="flex items-center gap-2 text-sm font-medium">
				{stage === "confirmed" ? (
					<span className="inline-flex size-5 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-500">
						<Check className="size-3.5" />
					</span>
				) : stage === "error" ? (
					<span className="size-5 rounded-full border-2 border-magenta" />
				) : reduced ? (
					<span className="size-5 animate-spin rounded-full border-2 border-border border-t-primary" />
				) : (
					<span className="orbital-loader size-5" />
				)}
				<span
					className={cn(
						stage === "confirmed" && "text-emerald-500",
						stage === "error" && "text-magenta",
					)}
				>
					{STAGE_LABEL[stage]}
				</span>
			</div>
			{!compact && (
				<div className="h-1.5 overflow-hidden rounded-full bg-muted/60">
					<div
						className={cn(
							"h-full rounded-full transition-[width] duration-500",
							stage === "error" ? "bg-magenta" : "bg-brand-gradient",
						)}
						style={{ width: `${progress}%` }}
					/>
				</div>
			)}
			{!compact && stage !== "error" && stage !== "confirmed" && (
				<ol className="flex justify-between font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
					{STAGE_ORDER.slice(0, 4).map((s, i) => (
						<li
							key={s}
							className={cn(i <= idx && "text-primary")}
						>
							{s === "confirmed" ? "ok" : s.slice(0, 4)}
						</li>
					))}
				</ol>
			)}
		</div>
	);
}

/** Simulate ICP update stages for demo writes. */
export async function runDemoUpdateStages(
	setStage: (s: UpdateStage) => void,
	opts?: { fail?: boolean },
): Promise<boolean> {
	const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
	setStage("signing");
	await wait(400);
	setStage("submitting");
	await wait(500);
	setStage("consensus");
	await wait(600);
	if (opts?.fail) {
		setStage("error");
		return false;
	}
	setStage("confirmed");
	await wait(400);
	return true;
}
